/**
 * Interstitial / routing page component (MA-1358, spec Confluence 561152044).
 *
 * Drives the link-off flow to a Partner Store, affiliate product or deal
 * banner ad (MA-2482):
 *
 * 1. LinkOff first (POST /shopify-app-proxy/linkoff/v1/{store|product|affiliate}
 *    with { pcId, portalId, countryCode, languageCode, volumeId|prodId }, or
 *    for a banner ad { …, adId, context: 'banner' }). The response envelope
 *    { data: { gotoURL, gotoName, logoURL, delay, showMaEzRegForm, … },
 *      meta: { status } } supplies the partner branding, the destination URL
 *    with tracking params and whether the email form shows. Its `delay` is
 *    deliberately ignored (MA-2815): nothing times a guest out of the capture
 *    form at all, so there is no wait left for the server to set.
 * 2. Logged-in customers skip capture (AC) and route directly. Guests with
 *    showMaEzRegForm get the email form and stay on it until they act; submit →
 *    EZReg → re-run LinkOff with the returned pcId so the destination is
 *    attributed; skip → route with the unattributed gotoURL (AC: no tracking).
 *    Only the routing state that follows counts down. The affiliate
 *    (banner) response carries no showMaEzRegForm, so in that mode a missing
 *    flag defaults to showing the form (an explicit false still skips it).
 * 3. Store conditions + the "up to X% Cashback" rate hydrate from the store
 *    detail API in parallel; both are optional decoration.
 * 4. The final hop is same-origin: `/go/<mode>/<id>` (Cloudflare
 *    shopify-linkoff-worker), which re-runs LinkOff server-side and 302s to the
 *    partner. Shopify's leave-store confirmation (2026-10) intercepts client-side
 *    navigations to the tracking host, but not a server redirect. The section
 *    setting `use_redirector` turns this off; `?go_error=` (the Worker's failure
 *    bounce) forces direct navigation once so a Worker outage can never loop.
 *
 * Both services are same-origin app-proxy routes (the proxy injects the
 * api_key — verified live on dev-us), so the theme sends no credentials and
 * guests can call them. Every failure path resolves to either the plain
 * redirect (when a gotoURL is already known) or the error state — the
 * customer is never stranded (AC: graceful degradation).
 *
 * The portal id is read from the `portal_id` cookie directly (with
 * window.PortalManager as fallback): portal-manager.js loads deferred AFTER
 * this module executes, so its global is not available during #init.
 */

import { Component } from '@theme/component';
import { getApiLocale } from '@theme/locale-map';
import { queryStoreDetail } from '@theme/query-store-detail';
import { queryProductDetail } from '@theme/query-product-detail';
import { API_ENDPOINTS } from '@theme/api-endpoints';
import { track, getLastSearch, ANALYTICS_EVENTS } from '@theme/analytics';
import { getCustomerRewardContext } from '@theme/price';
import { renderTextWithLineBreaks } from '@theme/utilities';

/** Endpoints live in the central module (MA-1398); keyed by page mode here. */
const LINKOFF_ENDPOINTS = {
  store: API_ENDPOINTS.linkoffStore,
  product: API_ENDPOINTS.linkoffProduct,
  affiliate: API_ENDPOINTS.linkoffAffiliate,
};

const EZREG_ENDPOINT = API_ENDPOINTS.ezRegistration;

/**
 * Params the pages accept, matched CASE-INSENSITIVELY (see #readIdParam).
 *
 * Three sources name these differently and all of them are in play: the mobile-app
 * request on MA-1358 asks for `?storeId=` / `?prodId=`, while the spec's Technical
 * Approach and MA's own LinkOff example both use `?storeID=` / `?prodID=` — which is
 * also what the affiliate product cards emit (ng-product-card.js, added by #660).
 * Accepting either casing keeps every caller working instead of silently failing to
 * read the id and dropping the customer on the error state.
 *
 * Affiliate (deal banner, MA-2482): deals.js emits `?adId=` — the affiliate
 * service's field name; `bannerId` is the HotDeals feed's name for the same
 * value and is accepted for hand-built links.
 */
const ID_PARAMS = {
  store: ['storeid', 'volumeid'],
  product: ['prodid', 'productid'],
  affiliate: ['adid', 'bannerid'],
};

/**
 * Countries where the interstitial does NOT show the distributor (PPC) IBV
 * reward line, even for an IBV-eligible customer whose store returns IBV data.
 * This is a page-local rule: the shared IBV gate (canShowIbv) already excludes
 * US/CA everywhere, but Indonesia DOES show IBV on its store/product cards, so
 * we exclude it here only — without touching the shared gate. Both the 2- and
 * 3-letter ISO spellings are listed so either form of countryCode matches.
 */
const IBV_EXCLUDED_COUNTRIES = new Set(['US', 'USA', 'CA', 'CAN', 'ID', 'IDN']);

/**
 * Markets where the store reward line uses the converted IBV fields
 * (`convIbvStandard` / `convIbvOnly`) instead of the raw `ibvStandard` /
 * `ibvOnly` — mirrors CONVERTED_IBV_COUNTRIES in ng-store-card.js. Both the 2-
 * and 3-letter ISO spellings are listed so either form of countryCode matches.
 */
const CONVERTED_IBV_COUNTRIES = new Set(['CA', 'AU', 'SG', 'HK', 'MY', 'CAN', 'AUS', 'SGP', 'HKG', 'MYS']);

const FETCH_TIMEOUT_MS = 15000;

/** AbortSignal.timeout needs feature-detection (Safari < 16). */
function fetchTimeoutSignal() {
  return typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal
    ? AbortSignal.timeout(FETCH_TIMEOUT_MS)
    : undefined;
}

/**
 * LinkOff does not always qualify the destination: the TWN partner store 263335 comes
 * back as `tracking.<host>/aff_c?…` with no scheme, while others in the same market are
 * fully qualified. `location.assign` resolves a scheme-less value against the CURRENT
 * page, so that lands the customer on a 404 on our own domain instead of the partner.
 * Treat a bare host as https rather than as a path on this store.
 * @param {string} url
 * @returns {string}
 */
function absoluteDestination(url) {
  const trimmed = String(url || '').trim();
  if (!trimmed) return '';
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
  // Protocol-relative is already absolute, it only needs the scheme.
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  return `https://${trimmed.replace(/^\/+/, '')}`;
}

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} partnerLogo
 * @property {HTMLElement} partnerName
 * @property {HTMLElement} progress
 * @property {HTMLElement} countdown
 * @property {HTMLFormElement} [captureForm]
 * @property {HTMLInputElement} [emailInput]
 * @property {HTMLElement} [emailError]
 * @property {HTMLButtonElement} [skip]
 * @property {HTMLElement} [offer]
 * @property {HTMLElement} [conditions]
 * @property {HTMLElement} [conditionsBody]
 * @property {HTMLElement} [errorMessage]
 *
 * @extends {Component<Refs>}
 */
class InterstitialRedirect extends Component {
  requiredRefs = ['partnerName', 'progress'];

  /** @type {string} */ #gotoUrl = '';
  /** @type {number | undefined} */ #timerId;
  /** @type {number | undefined} */ #countdownId;
  /** @type {boolean} */ #initialized = false;
  /** @type {boolean} */ #inFlight = false;
  /** @type {string} */ #errorText = '';
  /** @type {string} */ #mode = '';
  /** @type {string} */ #linkId = '';
  /** @type {string} */ #partnerName = '';
  /** @type {'not_shown' | 'submitted' | 'skipped'} */ #captureOutcome = 'not_shown';
  /** pcId LinkOff was (or will be) attributed to: the customer's, or EZReg's. */
  /** @type {string} */ #pcId = '';
  /** @type {{ countryCode: string, languageCode: string } | null} */ #locale = null;

  connectedCallback() {
    super.connectedCallback();
    // Theme-editor moves re-mount the same element; the flow must not restart
    // (duplicate listeners would fire duplicate EZReg registrations).
    if (this.#initialized) return;
    this.#initialized = true;
    this.#init().catch(() => this.#fail());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearTimers();
  }

  async #init() {
    const requested = this.dataset.mode || '';
    const mode = requested in LINKOFF_ENDPOINTS ? requested : 'store';
    const id = this.#readIdParam(mode);
    if (!id) return this.#fail();
    this.#mode = mode;
    this.#linkId = id;
    // The affiliate service takes adId as a JSON number; anything non-numeric
    // can only 4xx, so fail fast without the round-trip.
    if (mode === 'affiliate' && !/^\d+$/.test(id)) return this.#fail();

    // The storefront locale goes to every LinkOff body and the volumes hydrate
    // below as-is (MA-3007): the service falls back to the market's default
    // language itself for pairs it does not support, so the theme no longer
    // applies its own market/language overrides here.
    const locale = getApiLocale();
    this.#locale = locale;
    this.#pcId = this.#initialPcId();
    const linkoff = await this.#linkoff(mode, id, this.#pcId, locale);
    if (!linkoff) return this.#fail();

    this.#gotoUrl = absoluteDestination(linkoff.gotoURL);
    this.#partnerName = linkoff.gotoName || '';
    this.#renderBranding(linkoff);
    // Cashback ships inside the LinkOff response's `rewards` object for BOTH
    // store and product link-offs (verified live: product 1983497549 →
    // data.rewards.cashbackPercent). That is the primary source and the only
    // one a product interstitial has; the store-detail call below only fills
    // cashback in as a fallback (and always fetches the store conditions).
    const cashbackShown = this.#renderCashback(linkoff.rewards);
    if (mode === 'store') {
      // Landing on a store — the interstitial is where a store card actually
      // brings a shopper, so it reports the view the same way store-detail
      // does. Emitted off the LinkOff response rather than waiting for the
      // volumes hydrate below, which is decoration and is allowed to fail:
      // the view is real either way. `source` keeps the two landings separable,
      // because this one is a gate on the way out and store-detail is not.
      track(ANALYTICS_EVENTS.STORE_PAGE_VIEW, {
        volumeId: id,
        storeName: linkoff.gotoName || '',
        source: 'interstitial',
        hasLinkOff: true,
        loggedIn: this.dataset.loggedIn === 'true',
        responseOk: true,
        lastSearch: getLastSearch(),
      });
      this.#hydrateStoreExtras(id, cashbackShown, locale).catch(() => {});
    }
    // Product (affiliate/CPA) interstitials carry their IBV on the product
    // itself (product-detail priceInfo), so the PPC reward line is hydrated
    // from a product-detail fetch here — the store path does the equivalent
    // inside #hydrateStoreExtras. Affiliate (banner-ad) link-offs have neither
    // a store nor a product to hydrate from, so the LinkOff rewards are all
    // that page shows.
    else if (mode === 'product') this.#hydrateProductReward(id).catch(() => {});

    // Logged-in customers where no IBV line applies — retail customers, and
    // distributors in IBV-excluded countries — get the cashback reward line
    // straight from the LinkOff response. The store/product hydrate can't be
    // relied on for this: the volumes query returns no hit in some distributor
    // contexts, so #renderStoreReward never runs. LinkOff always carries the
    // customer's own cashback for the store or product being linked off, so it
    // is the dependable source for the cashback-only line. IBV-eligible
    // customers in allowed countries fall through to the hydrate path, which
    // adds the IBV rate the LinkOff response does not carry.
    if (this.dataset.loggedIn === 'true' && !this.#ibvRewardContext()) {
      this.#paintReward(getCustomerRewardContext(), this.#formatPercent(linkoff.rewards?.cashbackPercent), '');
    }

    // AC: authenticated customers never see email capture — even when the
    // pc_id metafield is missing (dev data gaps), they go straight to routing.
    // The affiliate (banner) response omits showMaEzRegForm entirely (verified
    // live on dev-us, MA-2482), so for that mode a missing flag means "show the
    // form" — the spec wants every guest offered enrolment. An explicit false
    // from the server still skips it, and store/product keep the strict check.
    const showForm =
      mode === 'affiliate' ? linkoff.showMaEzRegForm !== false : Boolean(linkoff.showMaEzRegForm);
    if (this.dataset.loggedIn === 'true' || !showForm) {
      return this.#route();
    }

    this.#startCapture(mode, id, locale);
  }

  /** The first LinkOff runs with the customer's pc_id when logged in, else empty (guest). */
  #initialPcId() {
    return this.dataset.loggedIn === 'true' ? this.dataset.pcId || '' : '';
  }

  #readIdParam(mode) {
    const accepted = ID_PARAMS[mode];
    // URLSearchParams.get() is case-sensitive, so walk the entries and compare
    // lowercased names instead of looking each spelling up.
    for (const [name, value] of new URLSearchParams(window.location.search)) {
      if (accepted.includes(name.toLowerCase()) && value.trim()) return value.trim();
    }
    return '';
  }

  /**
   * @param {'store' | 'product' | 'affiliate'} mode
   * @param {string} id - volumeId, prodId or adId as read from the URL.
   * @param {string} pcId
   * @param {{ countryCode: string, languageCode: string }} locale - from getApiLocale().
   * @returns {Promise<Record<string, any> | null>} the LinkOff `data` object, or null.
   */
  async #linkoff(mode, id, pcId, locale) {
    // languageCode goes on every mode (MA-3007): the service is adding a
    // language-dependent change and falls back to the store's default language
    // when the body omits it, so the storefront language must be passed
    // explicitly.
    /** @type {Record<string, unknown>} */
    const body = {
      pcId,
      portalId: this.#portalId(),
      countryCode: locale.countryCode,
      languageCode: locale.languageCode,
    };
    if (mode === 'store') body.volumeId = id;
    else if (mode === 'affiliate') {
      // Verified live on dev-us (MA-2482): adId is numeric and `context`
      // selects the banner flow.
      body.adId = Number(id);
      body.context = 'banner';
    } else body.prodId = id;

    try {
      const response = await fetch(LINKOFF_ENDPOINTS[mode], {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: fetchTimeoutSignal(),
      });
      // Read the envelope even on a non-2xx: LinkOff answers a gone/restricted
      // store with 405 + meta.statusText ("Store No Longer Available or
      // Restricted"), which names the reason far better than our generic copy.
      const payload = await response.json().catch(() => null);
      const meta = payload?.meta;
      const statusText = String(meta?.statusText || '').trim();
      if (Number(meta?.statusCode) === 405 && statusText) this.#errorText = statusText;
      if (!response.ok) return null;
      if (meta?.status !== 'Success' || !payload?.data?.gotoURL) return null;
      return payload.data;
    } catch {
      return null;
    }
  }

  /**
   * Cookie first: portal-manager.js loads deferred after this module runs, so
   * window.PortalManager is a fallback for late calls (EZReg re-LinkOff), not
   * a dependable source during #init.
   */
  #portalId() {
    const fromCookie = this.#readCookie('portal_id');
    if (fromCookie && /^[a-zA-Z0-9_-]{1,64}$/.test(fromCookie)) return fromCookie;
    try {
      return window.PortalManager?.getPortalId?.() || '';
    } catch {
      return '';
    }
  }

  #readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  }

  #renderBranding(data) {
    this.refs.partnerName.textContent = data.gotoName || '';
    const logo = this.refs.partnerLogo;
    if (logo && data.logoURL) {
      logo.src = data.logoURL;
      logo.alt = data.gotoName || '';
      logo.hidden = false;
    }
  }

  /**
   * Store conditions + cashback rate are decoration — failures stay silent.
   * @param {string} volumeId
   * @param {boolean} cashbackAlreadyShown - true when the LinkOff response
   *   already populated the offer line, so store-detail is conditions-only.
   * @param {{ countryCode: string, languageCode: string }} locale - the same
   *   storefront locale the LinkOff call used, so the page never mixes
   *   languages across the two calls.
   */
  async #hydrateStoreExtras(volumeId, cashbackAlreadyShown, locale) {
    const result = await queryStoreDetail({
      languageCode: locale.languageCode,
      countryCode: locale.countryCode,
      // The store-detail (volumes) endpoint carries cashback in the raw
      // `stdCashback` / `incCashback` fields; its customer-context `rewards[]`
      // array is usually empty for guests (the typical interstitial visitor).
      // Request all three so #bestCashbackPercent can fall back like ng-store-card.
      // ibvStandard / ibvOnly carry the distributor IBV rates for the PPC reward line;
      // convIbvStandard / convIbvOnly are the converted rates for CONVERTED_IBV_COUNTRIES.
      fields: [
        'volumeId',
        'storeName',
        'rewards',
        'stdCashback',
        'incCashback',
        'ibvStandard',
        'ibvOnly',
        'convIbvStandard',
        'convIbvOnly',
        'maStoreConditions',
      ],
      volumeIds: [String(volumeId)],
    });
    if (!result.ok || !result.data.store) return;
    const store = result.data.store;

    if (!cashbackAlreadyShown) {
      const percent = this.#bestCashbackPercent(store);
      const template = this.refs.offer?.dataset.template || '';
      if (percent && this.refs.offer && template.includes('[X]%')) {
        this.refs.offer.textContent = template.replace('[X]%', percent);
        this.refs.offer.hidden = false;
      }
    }

    this.#renderStoreReward(store);

    if (store.maStoreConditions && this.refs.conditions && this.refs.conditionsBody) {
      renderTextWithLineBreaks(this.refs.conditionsBody, store.maStoreConditions);
      this.refs.conditions.hidden = false;
    }
  }

  /**
   * Fills the "up to [X]% Cashback" offer line from the LinkOff response's
   * `rewards` object — the flat shape returned for both store and product
   * link-offs: `{ cashback, cashbackFormatted, cashbackPercent,
   * cashbackPercentFormatted }`. A non-positive rate leaves the default
   * no-rate copy untouched.
   * @param {Record<string, any> | undefined} rewards
   * @returns {boolean} true when a positive rate was rendered.
   */
  #renderCashback(rewards) {
    const num = parseFloat(String(rewards?.cashbackPercent ?? ''));
    const email = this.dataset.customerEmail || '';
    const percent = Number.isFinite(num) && num > 0 ? `${parseFloat(num.toFixed(2))}%` : '';
    const template = this.refs.offer?.dataset.template || '';
    if (percent && this.refs.offer && template.includes('[X]%')) {
      this.refs.offer.textContent = template.replace('[X]%', percent).replace('[email]', email);
      this.refs.offer.hidden = false;
      return true;
    }
    return false;
  }

  /**
   * Mirrors ng-store-card's extractCashback: type matches case-insensitively
   * ("Cashback"/"CASHBACK"), "up to" means the highest rate, and the value is
   * normalised ("2.50%" → "2.5%", bare "3" → "3%", non-positive → none). When
   * the customer-context `rewards[]` array yields nothing (empty for most
   * guests), fall back to the store's raw `incCashback` / `stdCashback` fields
   * — preferring Increased over Standard, exactly as the store card does.
   * @param {Record<string, any>} store
   * @returns {string} e.g. "2.5%", or '' when no usable rate exists.
   */
  #bestCashbackPercent(store) {
    let best = 0;
    for (const reward of store?.rewards || []) {
      if (!String(reward?.type || '').toLowerCase().includes('cashback')) continue;
      const value = parseFloat(String(reward?.percent ?? '').replace('%', ''));
      if (Number.isFinite(value) && value > best) best = value;
    }
    if (best <= 0) {
      const inc = parseFloat(String(store?.incCashback ?? ''));
      const std = parseFloat(String(store?.stdCashback ?? ''));
      const raw = Number.isFinite(inc) && inc > 0 ? inc : Number.isFinite(std) && std > 0 ? std : 0;
      if (raw > best) best = raw;
    }
    return best > 0 ? `${parseFloat(best.toFixed(2))}%` : '';
  }

  /**
   * IBV rate for the store, mirroring ng-store-card's extractIbv: prefer an
   * "ibv" entry in the customer-context `rewards[]` (already reflects the
   * customer's reward structure), then fall back to the raw fields — Increased
   * IBV (`ibvOnly`) when the customer's preference selects it, standard
   * (`ibvStandard`) otherwise. When `useConverted` is true (markets in
   * CONVERTED_IBV_COUNTRIES) the unconverted `rewards[]` entry is skipped and
   * the converted `convIbvOnly` / `convIbvStandard` fields are used, falling
   * back to the raw field only when the converted one is null/undefined.
   * @param {Record<string, any>} store
   * @param {boolean} preferIncreased - true for an increased-IBV customer.
   * @param {boolean} [useConverted=false] - read the converted IBV fields.
   * @returns {string} e.g. "0.39%", or '' when no usable rate exists.
   */
  #bestIbvPercent(store, preferIncreased, useConverted = false) {
    let best = 0;
    /** @param {string} rawKey @param {string} convKey */
    const field = (rawKey, convKey) => {
      if (useConverted && store?.[convKey] !== null && store?.[convKey] !== undefined) return store[convKey];
      return store?.[rawKey];
    };
    // The rewards[] 'ibv' entry carries the unconverted rate — skip it for converted markets.
    for (const reward of useConverted ? [] : store?.rewards || []) {
      // Exact 'ibv' — 'bv' is a substring of 'ibv', so includes() would over-match.
      if (String(reward?.type || '').toLowerCase() !== 'ibv') continue;
      const value = parseFloat(String(reward?.percent ?? '').replace('%', ''));
      if (Number.isFinite(value) && value > best) best = value;
    }
    if (best <= 0) {
      const increased = field('ibvOnly', 'convIbvOnly');
      const standard = field('ibvStandard', 'convIbvStandard');
      const primary = preferIncreased ? increased : standard;
      const secondary = preferIncreased ? standard : increased;
      const p = parseFloat(String(primary ?? ''));
      const s = parseFloat(String(secondary ?? ''));
      const raw = Number.isFinite(p) && p > 0 ? p : Number.isFinite(s) && s > 0 ? s : 0;
      if (raw > best) best = raw;
    }
    return best > 0 ? `${parseFloat(best.toFixed(2))}%` : '';
  }

  /**
   * The shared reward context when the distributor (PPC) reward line should
   * show, or null. Uses the SAME `getCustomerRewardContext()` as the
   * product/store cards for eligibility (canShowIbv), so the only page-local
   * rule is the IBV_EXCLUDED_COUNTRIES check.
   * @returns {ReturnType<typeof getCustomerRewardContext> | null}
   */
  #ibvRewardContext() {
    const ctx = getCustomerRewardContext();
    if (!ctx.canShowIbv) return null;
    if (IBV_EXCLUDED_COUNTRIES.has(String(ctx.countryCode).toUpperCase())) return null;
    return ctx;
  }

  /**
   * Normalises a numeric percentage to a display string ("2.50" → "2.5%",
   * non-positive → '').
   * @param {string | number} value
   * @returns {string}
   */
  #formatPercent(value) {
    const num = parseFloat(String(value ?? ''));
    return Number.isFinite(num) && num > 0 ? `${parseFloat(num.toFixed(2))}%` : '';
  }

  /**
   * Paints the reward line from whichever rates are available: the split
   * "Cashback! And IBV!" when the customer earns both, IBV only for an
   * increased-IBV customer, and Cashback only for a customer with no IBV to
   * show (a retail customer, or a distributor in a country where IBV is
   * hidden). An increased-IBV customer forgoes cashback where IBV is shown, so
   * their split line carries IBV only; where IBV is hidden they still get the
   * cashback line rather than nothing. Left hidden when no rate is available.
   * Purely additive: the existing Cashback offer in the capture form is untouched.
   * @param {ReturnType<typeof getCustomerRewardContext>} ctx
   * @param {string} cashbackStr - e.g. "10%", or '' when unavailable.
   * @param {string} ibvStr - e.g. "0.7%", or '' when unavailable.
   */
  #paintReward(ctx, cashbackStr, ibvStr) {
    const line = this.refs.rewardLine;
    if (!line) return;

    // An increased-IBV customer forgoes cashback for the higher IBV line — but
    // only where IBV is actually shown. In a country where the IBV line is
    // suppressed (no rate to render), dropping cashback too would leave them
    // with a blank reward line, so fall back to cashback there.
    const cashback = ctx.isIbvOnly && ibvStr ? '' : cashbackStr;
    const email = this.dataset.customerEmail || '';
    let text = '';
    if (cashback && ibvStr) {
      const template = line.dataset.templateSplit || '';
      if (template.includes('[X]%') && template.includes('[Y]%')) {
        text = template.replace('[X]%', cashback).replace('[email]', email).replace('[Y]%', ibvStr);
      }
    } else if (ibvStr) {
      const template = line.dataset.templateIbv || '';
      if (template.includes('[Y]%')) text = template.replace('[email]', email).replace('[Y]%', ibvStr);
    } else if (cashback) {
      const template = line.dataset.templateCashback || '';
      if (template.includes('[X]%')) text = template.replace('[X]%', cashback).replace('[email]', email);
    }

    if (!text) return;
    line.textContent = text;
    line.hidden = false;
  }

  /**
   * Reward line for a STORE interstitial, shown to logged-in customers during
   * the routing dwell (guests use the email-capture offer instead). An
   * IBV-eligible distributor in an allowed country gets Cashback + IBV (or IBV
   * only for an increased-IBV preference); everyone else logged-in — retail
   * customers, and distributors in the IBV-excluded countries — gets the
   * Cashback line. IBV/cashback rates come from the store-detail hit.
   * @param {Record<string, any>} store
   */
  #renderStoreReward(store) {
    if (this.dataset.loggedIn !== 'true') return;
    const ibvCtx = this.#ibvRewardContext();
    const ctx = ibvCtx || getCustomerRewardContext();
    const useConverted = ibvCtx
      ? CONVERTED_IBV_COUNTRIES.has(String(ibvCtx.countryCode || '').toUpperCase())
      : false;
    const ibv = ibvCtx ? this.#bestIbvPercent(store, ctx.isIbvOnly, useConverted) : '';
    this.#paintReward(ctx, this.#bestCashbackPercent(store), ibv);
  }

  /**
   * Reward line for a PRODUCT (affiliate/CPA) interstitial, shown to logged-in
   * customers during the routing dwell. Cashback and IBV both live in the
   * product-detail `priceInfo` — Increased IBV (`ibvOnlyPercent`) when the
   * customer's preference selects it, standard (`ibvStandardPercent`)
   * otherwise. An IBV-eligible distributor in an allowed country gets Cashback
   * + IBV (or IBV only); everyone else logged-in gets the Cashback line. Skips
   * the fetch for guests, who get the capture-form offer instead.
   * @param {string} prodId
   */
  async #hydrateProductReward(prodId) {
    if (this.dataset.loggedIn !== 'true') return;
    const ibvCtx = this.#ibvRewardContext();

    const result = await queryProductDetail({ productId: String(prodId) });
    if (!result.ok || !result.data.product) return;

    const priceInfo = result.data.product.product?.priceInfo || {};
    const ctx = ibvCtx || getCustomerRewardContext();
    const ibvValue = ibvCtx ? (ctx.isIbvOnly ? priceInfo.ibvOnlyPercent : priceInfo.ibvStandardPercent) : '';
    this.#paintReward(ctx, this.#formatPercent(priceInfo.standardCashBackPercent), this.#formatPercent(ibvValue));
  }

  #startCapture(mode, id, locale) {
    this.setAttribute('state', 'capture');
    if (this.refs.captureForm) this.refs.captureForm.hidden = false;

    // MA-2815 round 2 (Marc, 18 Sep): no countdown is started here. A guest is
    // never sent on by a timer — the offer stays up until they act on it, by
    // submitting the email or taking Skip, so the only way off this page is a
    // deliberate one. This retires both controls that used to end the wait:
    // LinkOff's own `delay` (60s on every market) and the capture_timeout
    // section setting that superseded it in round 1.
    //
    // Only guests reach #startCapture — #handleLinkoff routes logged-in
    // customers straight to #route() — so their routing_delay is unaffected.
    // The spinner is hidden in this state (see the section stylesheet): with
    // nothing counting down it would otherwise read as a stalled page.

    this.refs.skip?.addEventListener('click', () => {
      if (this.#inFlight) return;
      this.#captureOutcome = 'skipped';
      this.#route();
    });

    this.refs.captureForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      if (this.#inFlight) return;
      this.#submitEmail(mode, id, locale).catch(() => this.#route());
    });
  }

  async #submitEmail(mode, id, locale) {
    const input = this.refs.emailInput;
    if (!input) return this.#route();

    const email = input.value.trim();
    const valid = input.checkValidity() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) {
      input.setAttribute('aria-invalid', 'true');
      if (this.refs.emailError) this.refs.emailError.hidden = false;
      return; // AC: stay on the form until a valid email or an explicit skip.
    }
    input.removeAttribute('aria-invalid');
    if (this.refs.emailError) this.refs.emailError.hidden = true;

    this.#inFlight = true;
    this.#clearTimers();
    this.setAttribute('state', 'loading');
    if (this.refs.captureForm) this.refs.captureForm.hidden = true;

    // The email is captured the moment it is accepted, independently of whether
    // EZReg then succeeds — a lead the enrolment call loses is still a lead.
    track(ANALYTICS_EVENTS.EMAIL_LEAD, {
      leadSource: 'interstitial_ez_registration',
      leadMode: mode,
      submitMethod: 'async',
    });

    const creditUser = await this.#register(email, locale);
    if (creditUser) {
      // EZReg creating (or resolving) the Cashback account is the one account
      // registration the theme itself performs, so it is the one it can report;
      // see docs/analytics.md for the surfaces it cannot.
      track(ANALYTICS_EVENTS.ACCOUNT_REGISTRATION, {
        registrationMethod: 'ez_registration',
        registrationSource: 'SHPFYMA',
        registrationMode: mode,
      });
      // LinkOff's pcId is the numeric id; EZReg returns it C/R-prefixed as
      // credit_user. Persist the prefixed value for portal-manager attribution.
      this.#persistCreditUser(creditUser);
      const pcId = creditUser.replace(/^[CR]/, '');
      this.#pcId = pcId;
      // With the same-origin redirector the Worker makes the attributed LinkOff call
      // itself (pcId travels on the /go/ URL), so re-running it here would only add a
      // third trip. Direct navigation still needs the attributed gotoURL.
      if (!this.#useRedirector()) {
        const attributed = await this.#linkoff(mode, id, pcId, locale);
        if (attributed?.gotoURL) this.#gotoUrl = absoluteDestination(attributed.gotoURL);
      }
      // Set after the re-run: the destination reported is the attributed one.
      this.#captureOutcome = 'submitted';
    }
    // Registration failure is not a dead end — route with the URL we hold.
    this.#route();
  }

  /**
   * EZReg — registers (or logs in) the Cashback customer by email.
   * @returns {Promise<string>} the credit_user value ('' on failure).
   */
  async #register(email, locale) {
    const language = window.Theme?.locale?.language || document.documentElement.lang || 'en';
    const query = new URLSearchParams({
      site_country: locale.countryCode,
      site_type: 'SHP',
      lang_code: String(language).slice(0, 2).toLowerCase(),
    });
    try {
      const response = await fetch(`${EZREG_ENDPOINT}?${query}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          email_address: email,
          resident_country: locale.countryCode,
          registration_source: 'SHPFYMA',
          portal_id: this.#portalId(),
          product_country: locale.countryCode,
        }),
        signal: fetchTimeoutSignal(),
      });
      if (!response.ok) return '';
      const payload = await response.json();
      let creditUser = String(
        payload?.credit_user ?? payload?.data?.credit_user ?? payload?.data?.creditUser ?? ''
      ).toUpperCase();
      // The live EZReg response carries credit_user as the BARE numeric id
      // (verified on dev-us: 201, data.credit_user = digits, is_ppc false).
      // EZReg accounts are retail customers, so the portal-manager prefix is
      // 'C' (meta-tags.liquid builds C+pc_id for non-PPC the same way).
      if (/^\d{1,15}$/.test(creditUser)) creditUser = `C${creditUser}`;
      // Strict portal-manager shape (isValidCreditUser) so what we persist is
      // what the rest of the tracking stack accepts.
      return /^[CR]\d{1,15}$/.test(creditUser) ? creditUser : '';
    } catch {
      return '';
    }
  }

  /**
   * Same cookie portal-manager reads (`credit_user`) — and the same scoping:
   * Domain=<broadest settable registrable domain> + Secure, probed the way
   * portal-manager's setCookie does it. A host-only cookie here could never be
   * cleared by portal-manager's domain-scoped clearCookie and would shadow it.
   */
  #persistCreditUser(creditUser) {
    try {
      const value = encodeURIComponent(creditUser);
      const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      const parts = window.location.hostname.split('.');
      // Broadest first (marketamerica.com before dev-us-shopify.marketamerica.com):
      // browsers silently reject public-suffix domains (com, com.tw), so the first
      // suffix that verifies IS the registrable domain portal-manager also targets.
      for (let size = 2; size <= parts.length; size += 1) {
        const domain = parts.slice(-size).join('.');
        document.cookie = `credit_user=${value}; expires=${expires}; path=/; Domain=${domain}; SameSite=Lax${secure}`;
        if (this.#readCookie('credit_user') === creditUser) return;
      }
      // Last resort (e.g. localhost previews): host-only.
      document.cookie = `credit_user=${value}; expires=${expires}; path=/; SameSite=Lax${secure}`;
    } catch {
      // Cookie writes are best-effort; the gotoURL already carries tracking.
    }
  }

  #route() {
    if (!this.#gotoUrl) return this.#fail();
    this.#clearTimers();

    // The real link-off. This page — not a store-detail anchor — is where a
    // shopper actually leaves for the partner, so the event belongs here.
    // Emitted when routing is committed rather than inside the countdown
    // callback, so the push has the countdown to reach GTM before the browser
    // navigates away.
    this.#trackLinkOff();

    this.setAttribute('state', 'routing');
    if (this.refs.captureForm) this.refs.captureForm.hidden = true;
    const delaySeconds = Number(this.dataset.routingDelay) || 3;
    // Same-origin hop by default (see #goUrl): Shopify's leave-store confirmation
    // (exit-interstitial.js, 2026-10) intercepts a direct navigation to the partner's
    // tracking host, but not a server-side 302 from our own origin.
    const target = this.#useRedirector() ? this.#goUrl() : this.#gotoUrl;
    this.#startCountdown(delaySeconds, () => window.location.assign(target));
  }

  /**
   * On unless the section setting turns it off, or the Worker just bounced us back with
   * `go_error=` (its LinkOff failed): then navigate directly this once, so a Worker
   * outage degrades to today's behaviour instead of looping.
   */
  #useRedirector() {
    if (this.dataset.useRedirector === 'false') return false;
    return !new URLSearchParams(window.location.search).has('go_error');
  }

  /**
   * `/go/<mode>/<id>` on this origin. shopify-linkoff-worker calls LinkOff server-side
   * with the same pcId / portalId / language this page would have sent and 302s to the
   * partner. The page's other query params (credit_user, portal_id, utm_*) ride along
   * for attribution; the id params and go_error do not. The Worker never accepts a
   * destination URL, so nothing here can turn it into an open redirect.
   */
  #goUrl() {
    const params = new URLSearchParams(window.location.search);
    const drop = new Set([...Object.values(ID_PARAMS).flat(), 'go_error', 'pcid', 'portalid', 'lang']);
    for (const name of [...params.keys()]) {
      if (drop.has(name.toLowerCase())) params.delete(name);
    }
    if (this.#pcId) params.set('pcId', this.#pcId);
    const portalId = this.#portalId();
    if (portalId) params.set('portalId', portalId);
    if (this.#locale?.languageCode) params.set('lang', this.#locale.languageCode);
    const query = params.toString();
    return `/go/${this.#mode}/${encodeURIComponent(this.#linkId)}${query ? `?${query}` : ''}`;
  }

  /**
   * Reports the hand-off to the partner destination.
   *
   * `linkOffMode` separates the three flows this page drives (store, affiliate
   * product, banner ad) and `captureOutcome` records what the guest did with
   * the email offer, which is the difference between an attributed link-off and
   * an anonymous one.
   */
  #trackLinkOff() {
    let destinationHost = '';
    try {
      destinationHost = new URL(this.#gotoUrl, window.location.origin).host;
    } catch {
      // A malformed destination still reports the link-off, just without a host.
    }

    track(ANALYTICS_EVENTS.STORE_LINK_OFF, {
      linkOffUrl: this.#gotoUrl,
      linkOffMode: this.#mode,
      volumeId: this.#mode === 'store' ? this.#linkId : '',
      linkOffId: this.#linkId,
      storeName: this.#partnerName,
      destinationHost,
      captureOutcome: this.#captureOutcome,
      loggedIn: this.dataset.loggedIn === 'true',
      viaRedirector: this.#useRedirector(),
      lastSearch: getLastSearch(),
    });
  }

  #startCountdown(seconds, onDone) {
    this.#clearTimers();
    let remaining = Math.max(1, Math.round(seconds));
    const render = () => {
      if (this.refs.countdown) {
        this.refs.countdown.textContent = remaining > 0 ? String(remaining) : '';
      }
    };
    render();
    this.#countdownId = window.setInterval(() => {
      remaining -= 1;
      render();
    }, 1000);
    this.#timerId = window.setTimeout(onDone, seconds * 1000);
  }

  #clearTimers() {
    if (this.#timerId) window.clearTimeout(this.#timerId);
    if (this.#countdownId) window.clearInterval(this.#countdownId);
    this.#timerId = undefined;
    this.#countdownId = undefined;
  }

  #fail() {
    this.#clearTimers();
    this.setAttribute('state', 'error');
    if (!this.refs.errorMessage) return;
    // The server's reason wins over the generic fallback when it sent one.
    if (this.#errorText) this.refs.errorMessage.textContent = this.#errorText;
    this.refs.errorMessage.hidden = false;
  }
}

if (!customElements.get('interstitial-redirect')) {
  customElements.define('interstitial-redirect', InterstitialRedirect);
}
